/* eslint-disable no-console */

/**
 * Direct Connection Manager
 * Enhanced WebUSB/Serial connection management with retry logic
 * kware: مدير الاتصال المباشر المحسن مع معالجة الأخطاء وإعادة المحاولة
 */
class DirectConnectionManager {
  constructor() {
    this.activeConnections = new Map();
    this.connectionHistory = this.loadConnectionHistory();
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
  }

  /**
   * Enhanced USB printer connection with retry logic
   */
  async connectUSBPrinter(device = null, options = {}) {
    const maxRetries = options.maxRetries || this.retryAttempts;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`USB connection attempt ${attempt}/${maxRetries}`);

        const targetDevice = device || await this.requestUSBDevice();

        // Check if device is already connected
        if (targetDevice.opened) {
          await targetDevice.close();
        }

        await targetDevice.open();

        // Select configuration (usually 1)
        if (targetDevice.configuration === null) {
          await targetDevice.selectConfiguration(1);
        }

        // Claim the interface (usually 0)
        await targetDevice.claimInterface(0);

        const connection = {
          type: 'usb',
          device: targetDevice,
          id: `usb-${targetDevice.vendorId}-${targetDevice.productId}`,
          name: this.getDeviceName(targetDevice),
          connected: true,
          connectedAt: new Date(),
          lastUsed: new Date(),
          options: {
            vendorId: targetDevice.vendorId,
            productId: targetDevice.productId,
            interfaceNumber: 0
          }
        };

        this.activeConnections.set(connection.id, connection);
        this.saveConnectionHistory(connection);

        console.log('USB connection successful:', connection.name);
        return connection;
      } catch (error) {
        lastError = error;
        console.error(`USB connection attempt ${attempt} failed:`, error.message);

        if (attempt < maxRetries) {
          await this.delay(this.retryDelay * attempt);
        }
      }
    }

    throw new Error(`USB connection failed after ${maxRetries} attempts: ${lastError?.message}`);
  }

  /**
   * Enhanced Serial printer connection with configuration options
   */
  async connectSerialPrinter(port = null, options = {}) {
    const maxRetries = options.maxRetries || this.retryAttempts;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Serial connection attempt ${attempt}/${maxRetries}`);

        const targetPort = port || await navigator.serial.requestPort();

        const serialOptions = {
          baudRate: options.baudRate || 9600,
          dataBits: options.dataBits || 8,
          parity: options.parity || 'none',
          stopBits: options.stopBits || 1,
          flowControl: options.flowControl || 'none'
        };

        await targetPort.open(serialOptions);

        const connection = {
          type: 'serial',
          port: targetPort,
          id: `serial-${Date.now()}`,
          name: options.name || 'Serial Printer',
          connected: true,
          connectedAt: new Date(),
          lastUsed: new Date(),
          options: serialOptions
        };

        this.activeConnections.set(connection.id, connection);
        this.saveConnectionHistory(connection);

        console.log('Serial connection successful:', connection.name);
        return connection;
      } catch (error) {
        lastError = error;
        console.error(`Serial connection attempt ${attempt} failed:`, error.message);

        if (attempt < maxRetries) {
          await this.delay(this.retryDelay * attempt);
        }
      }
    }

    throw new Error(`Serial connection failed after ${maxRetries} attempts: ${lastError?.message}`);
  }

  /**
   * Enhanced data sending with error handling and retry
   */
  async sendData(connectionId, data, options = {}) {
    const connection = this.activeConnections.get(connectionId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    const maxRetries = options.maxRetries || 2;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Send data attempt ${attempt}/${maxRetries} to ${connection.name}`);

        if (connection.type === 'usb') {
          await this.sendUSBData(connection, data);
        } else if (connection.type === 'serial') {
          await this.sendSerialData(connection, data);
        }

        connection.lastUsed = new Date();
        connection.lastDataSize = data.length;

        return {
          success: true,
          timestamp: new Date(),
          dataSize: data.length,
          connection: connection.name
        };
      } catch (error) {
        lastError = error;
        console.error(`Send data attempt ${attempt} failed:`, error.message);

        // Try to reconnect if it's a connection error
        if (this.isConnectionError(error) && attempt < maxRetries) {
          console.log('Attempting to reconnect...');
          try {
            await this.reconnect(connectionId);
          } catch (reconnectError) {
            console.error('Reconnection failed:', reconnectError.message);
          }
        }
      }
    }

    throw new Error(`Send data failed after ${maxRetries} attempts: ${lastError?.message}`);
  }

  /**
   * Send data via USB
   */
  async sendUSBData(connection, data) {
    const { device, options } = connection;

    if (!device.opened) {
      throw new Error('USB device not connected');
    }

    const dataArray = typeof data === 'string' ?
      new TextEncoder().encode(data) : data;

    // Find the OUT endpoint (usually 1 or 2)
    const endpointNumber = options.endpointOut || 1;

    await device.transferOut(endpointNumber, dataArray);
  }

  /**
   * Send data via Serial
   */
  async sendSerialData(connection, data) {
    const { port } = connection;

    if (!port.writable) {
      throw new Error('Serial port not writable');
    }

    const writer = port.writable.getWriter();

    try {
      const dataArray = typeof data === 'string' ?
        new TextEncoder().encode(data) : data;

      await writer.write(dataArray);
    } finally {
      writer.releaseLock();
    }
  }

  /**
   * Reconnect to a previously connected device
   */
  async reconnect(connectionId) {
    const connection = this.activeConnections.get(connectionId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    // Disconnect first
    await this.disconnect(connectionId);

    // Reconnect based on type
    if (connection.type === 'usb') {
      return await this.connectUSBPrinter(connection.device, connection.options);
    } else if (connection.type === 'serial') {
      return await this.connectSerialPrinter(connection.port, connection.options);
    }

    throw new Error(`Unsupported connection type: ${connection.type}`);
  }

  /**
   * Disconnect from a device
   */
  async disconnect(connectionId) {
    const connection = this.activeConnections.get(connectionId);
    if (!connection) return;

    try {
      if (connection.type === 'usb' && connection.device.opened) {
        await connection.device.close();
      } else if (connection.type === 'serial' && connection.port.readable) {
        await connection.port.close();
      }

      connection.connected = false;
      connection.disconnectedAt = new Date();
    } catch (error) {
      console.error('Disconnect error:', error);
    } finally {
      this.activeConnections.delete(connectionId);
    }
  }

  /**
   * Test connection with a simple command
   */
  async testConnection(connectionId, testCommand = null) {
    const connection = this.activeConnections.get(connectionId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    try {
      // Default test commands based on printer type
      const defaultTestCommands = {
        'ZEBRA_ZPL': '^XA^FO50,50^A0N,50,50^FDTest^FS^XZ',
        'ESC_POS': '\x1B\x40Test\x0A\x0A', // ESC @ Test
        'BROTHER_PTOUCH': 'Test\x0D'
      };

      const command = testCommand || defaultTestCommands.ZEBRA_ZPL;

      await this.sendData(connectionId, command);

      return {
        success: true,
        message: 'Test print sent successfully',
        timestamp: new Date()
      };
    } catch (error) {
      return {
        success: false,
        message: error.message,
        timestamp: new Date()
      };
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus(connectionId) {
    const connection = this.activeConnections.get(connectionId);
    if (!connection) return null;

    return {
      id: connection.id,
      name: connection.name,
      type: connection.type,
      connected: connection.connected,
      connectedAt: connection.connectedAt,
      lastUsed: connection.lastUsed,
      uptime: Date.now() - connection.connectedAt.getTime()
    };
  }

  /**
   * Get all active connections
   */
  getActiveConnections() {
    return Array.from(this.activeConnections.values()).map(connection => ({
      id: connection.id,
      name: connection.name,
      type: connection.type,
      connected: connection.connected,
      connectedAt: connection.connectedAt,
      lastUsed: connection.lastUsed
    }));
  }

  /**
   * Request USB device with common printer filters
   */
  async requestUSBDevice() {
    return await navigator.usb.requestDevice({
      filters: [
        { vendorId: 0x0A5F }, // Zebra Technologies
        { vendorId: 0x04F9 }, // Brother Industries
        { vendorId: 0x04B8 }, // Epson
        { vendorId: 0x1504 }, // Citizen
        { vendorId: 0x0619 }, // Star Micronics
        { vendorId: 0x067B }, // Prolific Technology (USB-Serial)
        { vendorId: 0x0403 }, // FTDI (USB-Serial)
      ]
    });
  }

  /**
   * Get device name from USB device info
   */
  getDeviceName(device) {
    const vendors = {
      0x0A5F: 'Zebra',
      0x04F9: 'Brother',
      0x04B8: 'Epson',
      0x1504: 'Citizen',
      0x0619: 'Star',
      0x067B: 'Prolific',
      0x0403: 'FTDI'
    };

    const vendor = vendors[device.vendorId] || 'Unknown';
    return `${vendor} Printer (${device.productId?.toString(16) || 'Unknown'})`;
  }

  /**
   * Check if error is connection-related
   */
  isConnectionError(error) {
    const connectionErrors = [
      'NetworkError',
      'NotConnectedError',
      'InvalidStateError',
      'device not opened',
      'port not writable'
    ];

    return connectionErrors.some(errorType => error.name === errorType || error.message.includes(errorType));
  }

  /**
   * Delay utility for retry logic
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Save connection history to localStorage
   */
  saveConnectionHistory(connection) {
    try {
      const historyEntry = {
        id: connection.id,
        name: connection.name,
        type: connection.type,
        connectedAt: connection.connectedAt,
        options: connection.options
      };

      this.connectionHistory.unshift(historyEntry);

      // Keep only last 10 connections
      this.connectionHistory = this.connectionHistory.slice(0, 10);

      localStorage.setItem('folio-connection-history', JSON.stringify(this.connectionHistory));
    } catch (error) {
      console.error('Failed to save connection history:', error);
    }
  }

  /**
   * Load connection history from localStorage
   */
  loadConnectionHistory() {
    try {
      const history = localStorage.getItem('folio-connection-history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Failed to load connection history:', error);
      return [];
    }
  }

  /**
   * Get connection history
   */
  getConnectionHistory() {
    return this.connectionHistory;
  }

  /**
   * Cleanup all connections
   */
  async cleanup() {
    const disconnectPromises = Array.from(this.activeConnections.keys())
      .map(connectionId => this.disconnect(connectionId));

    await Promise.allSettled(disconnectPromises);
    this.activeConnections.clear();
  }
}

export default DirectConnectionManager;
